import { isAxiosError } from "axios"
import { format, getISOWeek, getISOWeekYear } from "date-fns"
import { useTranslation } from "react-i18next"

import {
  useAddToCartCompleteEvent,
  useDataLayer,
  type AddToCartProduct,
} from "@/features/DataLayer"
import { useSelectedResort } from "@/hooks"
import { useDateParam } from "@/hooks/useSkipassFlow"
import { useCart } from "@/queries/hooks"
import { useSkipassStore } from "@/store/skipassStore"
import { AddToCartRequest, SkiPassItem, SkiPassTicketItem } from "@/types/Cart"
import { PassHolderFormState } from "@/types/PassHolder"
import { showCart } from "@/utils/appshellEvents"

export const useCartActions = () => {
  const { t } = useTranslation()
  const { startDate } = useDateParam()
  const { selectedProducts } = useSkipassStore()
  const { addToCart, isLoading, error } = useCart()
  const { selectedResort } = useSelectedResort()
  const runAddToCartComplete = useAddToCartCompleteEvent()
  const viewShoppingcartSkipassEvent = useDataLayer("view_shoppingcart_skipass")

  const getProductPrice = (productPriceId: string) => {
    const product = selectedProducts.find(
      (p) => p.productPriceId === productPriceId
    )
    return product?.price ?? 0
  }

  const getCurrencyCode = () => {
    return selectedProducts[0]?.priceSuffix ?? ""
  }

  const createAddToCartProducts = (
    passHolders: PassHolderFormState[]
  ): AddToCartProduct[] => {
    return passHolders.flatMap((passHolder) => {
      const skipassProduct: AddToCartProduct = {
        id: passHolder.analyticsId ?? null,
        name: passHolder.analyticsName ?? "",
        price: getProductPrice(passHolder.productPriceId).toFixed(2),
        category: "SkiPass" as const,
        quantity: 1,
      }

      if (passHolder.insurance && passHolder.insurancePrice) {
        const insuranceProduct: AddToCartProduct = {
          id: passHolder.insuranceAnalyticsId ?? null,
          name: passHolder.insuranceAnalyticsName ?? "",
          price: passHolder.insurancePrice.toFixed(2),
          category: "Insurance" as const,
          quantity: 1,
        }
        return [skipassProduct, insuranceProduct]
      }

      return [skipassProduct]
    })
  }

  const createAddToCartProductsFromTickets = (
    tickets: { productPriceId: string }[]
  ): AddToCartProduct[] => {
    return tickets.map((ticket) => {
      const product = selectedProducts.find(
        (p) => p.productPriceId === ticket.productPriceId
      )
      const item: AddToCartProduct = {
        id: product?.analyticsId ?? null,
        name: product?.analyticsName ?? "",
        price: (product?.price ?? 0).toFixed(2),
        category: "Ticket" as const,
        quantity: 1,
      }
      return item
    })
  }

  const cartErrorMessage = (): string | null => {
    if (!error) return null
    if (isAxiosError(error)) {
      return t("passHolderDetails.form.errors.addToCartFailed")
    }
    return error.message || t("passHolderDetails.form.errors.addToCartFailed")
  }

  const createSkipassItem = (passHolder: PassHolderFormState): SkiPassItem => {
    const hasCoupon =
      !!passHolder.memberCoupon?.couponCode &&
      !!passHolder.memberCoupon?.customerId

    const exitingPhotoId =
      passHolder.exitingPhotoId !== null &&
      passHolder.exitingPhotoId !== undefined
        ? passHolder.exitingPhotoId
        : null
    const newPhotoFileId = exitingPhotoId
      ? null
      : passHolder.passHolderImage?.id || null

    const baseSkipassItem = {
      id: passHolder.passHolder.id,
      loadSkipass: passHolder.passSelection === "existing",
      skipassNumber: passHolder.wtpNumber || "",
      newPhotoFileId,
      newCertificateFileId: passHolder.personCertificate?.id || null,
      firstName: passHolder.passHolder.firstName,
      lastName: passHolder.passHolder.lastName,
      birthDate: passHolder.passHolder.birthDate,
      productId: passHolder.productId,
      resortId: passHolder.resortId,
      arrivalDate: format(startDate, "yyyy-MM-dd"),
      insurance: passHolder.insurance ?? false,
      bookingLength: passHolder.bookingLengthInDays,
      birthdateUpdatedOnLoad: false,
      firstNameUpdatedOnLoad: false,
      lastNameUpdatedOnLoad: false,
      exitingPhotoId,
      exitingCertificateId: null,
      isSubscriptionProduct: passHolder.isSubscriptionProduct ?? false,
      subscriptionConfigurationId:
        passHolder.subscriptionConfigurationId ?? null,
    }

    const couponFields = hasCoupon
      ? {
          mySkistarUserCoupon: {
            firstName: passHolder.passHolder.firstName,
            lastName: passHolder.passHolder.lastName,
            birthDate: passHolder.passHolder.birthDate,
            couponCode: passHolder.memberCoupon?.couponCode ?? "",
            couponDesc: passHolder.memberCoupon?.couponDesc ?? "",
            customerId: passHolder.memberCoupon?.customerId ?? 0,
          },
        }
      : {}

    return {
      ...baseSkipassItem,
      ...couponFields,
    }
  }

  const createSkiPassTicket = (
    productPriceId: string,
    startDate: Date
  ): SkiPassTicketItem => {
    return {
      arrivalDate: format(startDate, "yyyy-MM-dd"),
      productPriceId: productPriceId,
    } as SkiPassTicketItem
  }

  const buildCartRequest = (
    passHolders: PassHolderFormState[],
    skiPassTickets: { productPriceId: string }[]
  ): AddToCartRequest => {
    const skiPassItems = passHolders.map(createSkipassItem)

    const skiPassTicketItems =
      skiPassTickets?.map((ticket) =>
        createSkiPassTicket(ticket.productPriceId, startDate)
      ) || []

    return {
      skiPassItems: skiPassItems,
      skiPassTicketItems: skiPassTicketItems,
    }
  }

  const openCart = (numPersons: number, numDays: number) => {
    const week = getISOWeek(startDate)
    const weekYear = getISOWeekYear(startDate)
    const dataLayerEventWeek = `${weekYear}${String(week).padStart(2, "0")}`
    const addMoreUrl = `${window.location.pathname}?date=${format(startDate, "yyyy-MM-dd")}`
    showCart({
      addMoreCallback: () => {
        window.location.href = addMoreUrl
      },
      goToCartDataLayerEvent: () => {
        viewShoppingcartSkipassEvent({
          NumPersons: numPersons.toString(),
          AccommodationArrivalDate: format(startDate, "yyyy-MM-dd"),
          AccommodationLength: numDays.toString(),
          AccommodationWeek: dataLayerEventWeek,
          AccommodationResort: selectedResort?.name ?? "",
        })
      },
    })
  }

  const addSkipassItemsToCart = (passHolders: PassHolderFormState[]) => {
    const isSubscription = passHolders[0]?.isSubscriptionProduct ?? false

    addToCart(
      {
        request: buildCartRequest(passHolders, []),
      },
      {
        onSuccess: (response) => {
          const analyticItems = createAddToCartProducts(passHolders)
          const curr = getCurrencyCode()
          runAddToCartComplete({
            products: analyticItems,
            currencyCode: curr,
            numPersons: passHolders.length,
          })

          if (isSubscription && response?.CartUrl) {
            window.location.href = response.CartUrl
          } else {
            openCart(
              passHolders.length,
              passHolders[0]?.bookingLengthInDays ?? 0
            )
          }
        },
      }
    )
  }

  const addTicketsToCart = (productPriceIds: string[]) => {
    const tickets = productPriceIds.map((id) => ({ productPriceId: id }))

    addToCart(
      {
        request: buildCartRequest([], tickets),
      },
      {
        onSuccess: () => {
          const analyticItems = createAddToCartProductsFromTickets(tickets)
          const curr = getCurrencyCode()
          runAddToCartComplete({
            products: analyticItems,
            currencyCode: curr,
            numPersons: tickets.length,
          })
          openCart(
            tickets.length,
            selectedProducts[0]?.bookingLengthInDays ?? 0
          )
        },
      }
    )
  }

  return {
    isLoading,
    error: cartErrorMessage(),
    addSkipassItemsToCart,
    addTicketsToCart,
  }
}
